import { useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/shadcn/ui/table'
import { cn } from '~/shadcn/lib/utils'
import { Button } from '~/shadcn/ui/button/button'
import { Spinner } from '~/shadcn/ui/spinner'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import { CopyIcon } from '~/@mid_ui/icons/CopyIcon'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { SortArrows, type SortDir } from '~/@mid_ui/sort-arrows/SortArrows'
import { getAmbassadorLink } from '~/@biz_ambassador/code/getAmbassadorLink'

export type MyAmbassadorItem = {
  id: string
  name: string
  clients: number
  /** invested amount in cents */
  invested: number
  code: string
  active: boolean
  createdAt: string
}

type SortKey = 'clients' | 'invested'

type Props = {
  items: Array<MyAmbassadorItem>
  isLoading?: boolean
  onSelect?: (item: MyAmbassadorItem) => void
}

export function AmbassadorsTable({ items, isLoading, onSelect }: Props) {
  const { setNotification } = useLocalNotifications()
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const sortedItems = useMemo(() => {
    if (!sortKey) return items
    return [...items].sort((a, b) => {
      const diff = a[sortKey] - b[sortKey]
      return sortDir === 'asc' ? diff : -diff
    })
  }, [items, sortKey, sortDir])

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  async function copyCode(code: string) {
    await navigator.clipboard?.writeText(getAmbassadorLink(code))
    setNotification('success', 'Odkaz bol skopírovaný.')
  }

  return (
    <div className="overflow-hidden border border-foreground">
      <Table>
        <TableHeader className="h-[45px]">
          <TableRow className="border-b-0 bg-foreground ">
            <TableHead className="px-4! font-medium text-white">
              Referencia
            </TableHead>

            <TableHead className="px-4! font-medium text-white">
              <button
                type="button"
                onClick={() => toggleSort('clients')}
                className="flex items-center justify-self-center">
                Klienti
                <SortArrows active={sortKey === 'clients'} dir={sortDir} />
              </button>
            </TableHead>

            <TableHead className="px-4! font-medium text-white">
              <button
                type="button"
                onClick={() => toggleSort('invested')}
                className="flex items-center justify-self-center">
                Investovali
                <SortArrows active={sortKey === 'invested'} dir={sortDir} />
              </button>
            </TableHead>

            <TableHead className="px-4! font-medium text-white">Kód</TableHead>

            <TableHead className="px-4! font-medium text-white">
              Akcia
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={5} className="h-20 text-center">
                <Spinner className="size-6 mx-auto" />
              </TableCell>
            </TableRow>
          )}

          {!isLoading && sortedItems.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="h-20 text-center text-sm font-medium text-muted">
                Zatiaľ nemáte žiadnych ambasádorov.
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            sortedItems.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  'h-[45px]',
                  'text-sm font-medium',
                  '[&_td]:border-b [&_td]:border-foreground',
                  'last:[&_td]:border-b-0',
                  !row.active && 'text-muted'
                )}>
                <TableCell className="px-4!">
                  <div className="flex items-center gap-2">
                    <span>{row.name}</span>
                    {!row.active && (
                      <span className="rounded-full bg-muted-light px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-dark">
                        Neaktívny
                      </span>
                    )}
                  </div>
                </TableCell>

                <TableCell className="px-4!">
                  <div className="flex justify-center">{row.clients}</div>
                </TableCell>

                <TableCell className="px-4!">
                  <CurrencyFormatter
                    value={row.invested}
                    className="text-base font-medium text-center"
                    hideDecimal
                  />
                </TableCell>

                <TableCell className="px-4! py-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!row.active}
                    onClick={() => copyCode(row.code)}
                    className={cn(
                      'size-8',
                      row.active ? 'text-accent' : 'text-muted-foreground'
                    )}>
                    <CopyIcon className="size-6" />
                  </Button>
                </TableCell>

                <TableCell className="px-4!">
                  <a
                    onClick={() => onSelect?.(row)}
                    className="cursor-pointer font-bold text-accent underline">
                    Detail
                  </a>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  )
}
